// CustomStats.tsx
import React from 'react';
import { useSearchResults, useSearchFilters, useSearchQuery } from '../contexts/SearchContext';
import { useIntl, FormattedMessage } from 'react-intl';
import { Info } from '@phosphor-icons/react';
import { LazyTooltip } from '../design-system/Tooltip';
import { generateStatsText, showFinlandSubtitle as computeShowFinlandSubtitle } from '../utils/stats/generateStatsText';
import type { StatsLocale } from '../utils/stats/generateStatsText';
import { Msg } from '../i18n-review/client/Msg';
import { buildActiveFiltersFromInitialFilters } from '../utils/seo/build-active-filters';
import { buildFilterTagSuffix } from '../utils/seo/generate-srp-seo-meta';

/** Filter types `generateStatsText` already folds into its core phrase (location, company,
 *  companyIndustry) — everything else gets appended as a plain tag-list suffix instead, so the
 *  H1 reflects the full applied filter set without touching generateStatsText's tested phrasing. */
const STATS_TEXT_COVERED_FILTER_TYPES = new Set(['location', 'company', 'companyIndustry']);

/**
 * Presentational display for stats: same structure always; only the strings change.
 * When finlandSubtitle is provided (static SRP), no FormattedMessage; otherwise uses react-intl.
 */
export interface CustomStatsDisplayProps {
  statsText: string;
  showFinlandSubtitle: boolean;
  /** When provided, render this instead of FormattedMessage (for static SSR, no IntlProvider). */
  finlandSubtitle?: string;
  /**
   * "· N new this week" clause (default homepage view only), shown inline after the headline. Pass
   * the pre-formatted string for static SSR (no IntlProvider), or omit to let the live/context path
   * compute it from the number below.
   */
  recentJobsSubtitle?: string | null;
  /** Live count for the FormattedMessage path (ignored when recentJobsSubtitle is provided). */
  recentJobsCount?: number | null;
}

/**
 * Headline row: H1 job count (untouched — same element, same text) with the "new this week" clause
 * and the Finland-availability note as inline siblings, not separate stacked paragraphs. The Finland
 * note collapses into an (i) tooltip trigger whose accessible name and SSR content is the note text
 * itself (a sr-only span inside the button), so crawlers and screen readers still get the full copy.
 */
export const CustomStatsDisplay: React.FC<CustomStatsDisplayProps> = ({
  statsText,
  showFinlandSubtitle,
  finlandSubtitle,
  recentJobsSubtitle,
  recentJobsCount,
}) => {
  const finlandSubtitleContent =
    finlandSubtitle != null ? (
      finlandSubtitle
    ) : (
      <Msg
        id="stats.finlandJobsSubtitle"
        defaultMessage="Jobs available in Finnish or remotely to Finnish residents"
        description="Subtitle explaining that jobs are available in Finnish or remotely to Finnish residents, shown below the job count when displaying all jobs in Finland"
      />
    );

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <h1 className="font-bold text-sm @lg:text-base">
        {statsText}
      </h1>
      {(recentJobsSubtitle != null || typeof recentJobsCount === 'number') && (
        <span className="text-xs @lg:text-sm font-normal text-gray-500 dark:text-gray-400">
          {recentJobsSubtitle != null ? (
            recentJobsSubtitle
          ) : (
            <Msg
              id="stats.newThisWeekInline"
              defaultMessage="· {count, number} new this week"
              description="Inline clause shown right after the job count headline, showing how many of those jobs were published in the last 7 days"
              values={{ count: recentJobsCount }}
            />
          )}
        </span>
      )}
      {showFinlandSubtitle && (
        <LazyTooltip content={finlandSubtitleContent} position="bottom">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-0.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#3B5E45] dark:focus:ring-[#98CFA8] cursor-pointer"
          >
            <Info className="h-4 w-4" weight="bold" aria-hidden />
            <span className="sr-only">{finlandSubtitleContent}</span>
          </button>
        </LazyTooltip>
      )}
    </div>
  );
};

export interface CustomStatsProps {
  /** When provided, display this string instead of computing from SearchContext (static/SSR). */
  statsText?: string;
  /** When provided with statsText, whether to show the Finland jobs subtitle (static/SSR). */
  showFinlandSubtitle?: boolean;
  /** When provided with statsText (static SRP), Finland subtitle string so no FormattedMessage is used. */
  finlandSubtitle?: string;
  /** When provided with statsText (static SRP), pre-formatted recent-jobs subtitle (no FormattedMessage). */
  recentJobsSubtitle?: string | null;
}

/**
 * When statsText (and optionally showFinlandSubtitle) are provided, renders the display only (no hooks).
 * When finlandSubtitle is also provided, no FormattedMessage for the subtitle (static SSR).
 */
const CustomStats: React.FC<CustomStatsProps> = (props) => {
  const {
    statsText: staticStatsText,
    showFinlandSubtitle: staticShowFinlandSubtitle,
    finlandSubtitle,
    recentJobsSubtitle,
  } = props;

  if (staticStatsText !== undefined) {
    return (
      <CustomStatsDisplay
        statsText={staticStatsText}
        showFinlandSubtitle={Boolean(staticShowFinlandSubtitle)}
        finlandSubtitle={finlandSubtitle}
        recentJobsSubtitle={recentJobsSubtitle}
      />
    );
  }

  return <CustomStatsWithContext />;
};

function CustomStatsWithContext(): React.ReactElement {
  const { results } = useSearchResults();
  const { filters } = useSearchFilters();
  const { query } = useSearchQuery();
  const intl = useIntl();

  const nbHits = Number(results?.nbHits ?? 0);
  const locale = intl.locale as StatsLocale;
  const filtersObj = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};
  const searchQuery = typeof query === 'string' ? query : '';
  const recentJobsCount =
    typeof (results as Record<string, unknown> | null)?.recentJobsCount === 'number'
      ? ((results as Record<string, unknown>).recentJobsCount as number)
      : null;

  const coreStatsText = generateStatsText({ nbHits, filters: filtersObj, query: searchQuery, locale });
  const extraFilters = buildActiveFiltersFromInitialFilters(filtersObj).filter(
    (f) => !STATS_TEXT_COVERED_FILTER_TYPES.has(f.type)
  );
  const statsText = coreStatsText + buildFilterTagSuffix(extraFilters, locale);
  const showFinlandSubtitle = computeShowFinlandSubtitle(coreStatsText, filtersObj, searchQuery);

  return (
    <CustomStatsDisplay
      statsText={statsText}
      showFinlandSubtitle={showFinlandSubtitle}
      recentJobsCount={recentJobsCount}
    />
  );
}

export default CustomStats;
